import { Badge, Center, Divider, Group, Loader, Stack, Text } from '@mantine/core';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { InfoRow } from 'components/InfoRow';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import AttachedInvoicesList from 'components/shared/AttachedInvoices';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { upperFirst } from 'lodash-es';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import Amount from 'shared/components/Amount';
import { useLoadThirdPartyCase } from 'shared/hooks/use-load-third-party-case';
import { useMarkConversationAsRead } from 'shared/hooks/use-mark-conversation-as-read';
import type { ThirdPartyCaseTypes } from 'types/index';
import { ActionsPanel } from './ActionsPanel';
import { ConversationPanel } from './ConversationPanel';

const STATUS_CONFIG = {
  close: { labelKey: i18nKeys.THIRD_PARTY_CASES.CLOSED, color: 'green' },
  open: { labelKey: i18nKeys.THIRD_PARTY_CASES.STARTED, color: 'orange' },
  notOpen: { labelKey: i18nKeys.THIRD_PARTY_CASES.NOT_STARTED, color: 'blue' },
} as const;

const CASE_TYPE_KEYS = {
  lawyer: i18nKeys.THIRD_PARTY_CASES.CASE_TYPE.LAWYER,
  bailiff: i18nKeys.THIRD_PARTY_CASES.CASE_TYPE.BAILIFF,
  debt_collector: i18nKeys.THIRD_PARTY_CASES.CASE_TYPE.DEBT_COLLECTOR,
} as const;

export function ThirdPartyCase() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { markConversationAsRead } = useMarkConversationAsRead();

  const { thirdPartyCase, isThirdPartyCaseLoading } = useLoadThirdPartyCase(Number(id));

  useEffect(() => {
    if (thirdPartyCase?.invoiceFeedback?.conversation) {
      markConversationAsRead({ id: thirdPartyCase.invoiceFeedback.conversation.id });
    }
  }, [markConversationAsRead, thirdPartyCase?.invoiceFeedback?.conversation]);

  if (isThirdPartyCaseLoading || thirdPartyCase == null) {
    return (
      <Center mih={400}>
        <Loader />
      </Center>
    );
  }

  const caseTypeLabel = t(CASE_TYPE_KEYS[thirdPartyCase.caseType as ThirdPartyCaseTypes]);
  const caseReference = thirdPartyCase.caseId || thirdPartyCase.invoiceReference;

  const caseStatus = thirdPartyCase.closed ? 'close' : thirdPartyCase.started ? 'open' : 'notOpen';
  const { labelKey, color } = STATUS_CONFIG[caseStatus];

  const invoiceFeedbackWithConversation = thirdPartyCase.invoiceFeedback ?? null;

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle>
        <PageTitle.Back />
        <Group gap="sm">
          {t(i18nKeys.THIRD_PARTY_CASES.MODAL_TITLE, {
            case_type: caseTypeLabel,
            case_reference: caseReference,
          })}
          <Badge color={color} variant="light" radius="xl">
            {t(labelKey)}
          </Badge>
        </Group>
      </PageTitle>

      <Panel title={t(i18nKeys.THIRD_PARTY_CASES.CASE_INFORMATION)} withBorder>
        <Group align="flex-start" gap={0}>
          <Stack flex={1} gap="sm">
            {thirdPartyCase.tpOwner && (
              <InfoRow label={<Text c="dimmed">{caseTypeLabel}</Text>}>
                <Text fw={500}>{upperFirst(thirdPartyCase.tpOwner)}</Text>
              </InfoRow>
            )}
            <InfoRow label={<Text c="dimmed">{t(i18nKeys.THIRD_PARTY_CASES.REFERENCE)}</Text>}>
              <Text fw={500}>{upperFirst(thirdPartyCase.caseId)}</Text>
            </InfoRow>
            <InfoRow label={<Text c="dimmed">{t(i18nKeys.CREATION_DATE)}</Text>}>
              <Text fw={500}>{dayjs(thirdPartyCase.createdAt).format('L')}</Text>
            </InfoRow>
            <InfoRow label={<Text c="dimmed">{t(i18nKeys.LAST_UPDATE)}</Text>}>
              <Text fw={500}>
                {thirdPartyCase.lastUpdate ? dayjs(thirdPartyCase.lastUpdate).format('L LT') : '-'}
              </Text>
            </InfoRow>
          </Stack>

          <Divider orientation="vertical" mx="lg" />

          <Stack flex={1} gap="sm">
            <InfoRow label={<Text c="dimmed">{t(i18nKeys.COMMON.CLIENT)}</Text>}>
              <ClientHoverCard
                debtorId={thirdPartyCase.debtor.id}
                debtorName={thirdPartyCase.debtor.fullName}
              />
            </InfoRow>
            <InfoRow
              label={<Text c="dimmed">{t(i18nKeys.FORM.CHOOSE_INVOICES.AMOUNT_CLAIMED)}</Text>}
            >
              <Amount value={thirdPartyCase.totalTvac} />
            </InfoRow>
            <InfoRow
              label={<Text c="dimmed">{t(i18nKeys.INVOICING.TOTAL_OUTSTANDING_PAYMENTS)}</Text>}
            >
              <Text fw={700}>
                <Amount value={thirdPartyCase.totalRemaining} />
              </Text>
            </InfoRow>
          </Stack>
        </Group>
      </Panel>

      {thirdPartyCase.attachedInvoices && (
        <AttachedInvoicesList invoices={thirdPartyCase.attachedInvoices} />
      )}

      {invoiceFeedbackWithConversation && (
        <Panel title={t(i18nKeys.THIRD_PARTY_CASES.CONVERSATION)} withBorder>
          <ConversationPanel invoiceFeedback={invoiceFeedbackWithConversation} />
        </Panel>
      )}

      <ActionsPanel />
    </Stack>
  );
}
